'use client';

import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useIntl } from 'react-intl';

import messages from '@/components/authForm/GoogleAuthButton/messages';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAuth } from '@/context/auth/AuthProvider';
import { CalmAuthMethods } from '@/utils/privacyCookies';

import { useSisu } from '../Provider';
import { type MessageNode } from '../utils';
import { Icon, Wrapper } from './styles';

export type GoogleAuthButtonProps = {
	/**
	 * @defaultValue
	 * ```tsx
	 * "sisu-google-auth-button"
	 * ```
	 */
	'data-testid'?: string;

	/**
	 * @defaultValue
	 * ```tsx
	 * false
	 * ```
	 */
	hideMessage?: boolean;

	message?: MessageNode;
};

export default function GoogleAuthButton({
	'data-testid': testId = 'sisu-google-auth-button',
	hideMessage = false,
	message,
}: GoogleAuthButtonProps) {
	const { authenticate } = useAuth();
	const { formatMessage } = useIntl();
	const { authPrompt } = useAuthPrompt();
	const { isMarketingOptInSelected } = useSisu();

	const handleClick = () => {
		authenticate({
			authMethod: CalmAuthMethods.Google,
			marketingOptIn: isMarketingOptInSelected,
		});
	};

	if (!message) {
		message = formatMessage(authPrompt === 'login' ? messages.signInCta : messages.continueCta);
	}

	return (
		<Wrapper
			backgroundColor="white"
			contentAlignment="center"
			data-testid={testId}
			onPress={handleClick}
			role="link"
			$noPadding={hideMessage}
			textColor="black"
			borderColor="gray3"
		>
			<Icon $noMargin={hideMessage} />
			{hideMessage ? <VisuallyHidden>{message}</VisuallyHidden> : message}
		</Wrapper>
	);
}
